import type {
  ActivitySeriesResponse,
  DashboardResponse,
  FilterOptionsResponse,
  GenerateInsightsReportResponse,
  InsightsReport,
  ListInsightsReportsParams,
  ListInsightsReportsResponse,
  AgentActivity,
  AgentKeys,
  GetNeedsALookResponse,
  GetProjectHeatmapResponse,
  ListProjectReadinessResponse,
  ListSemanticPatternsResponse,
  ListConstraintCandidatesResponse,
  SpendSummary,
  SyncStatus,
  ConnectionStatus,
  UsageEconomics,
  UsageResponse,
  UserSettings,
  ListSessionsParams,
  ListSessionsResponse,
  ListTasksParams,
  ListTasksResponse,
  ReprocessingStatus,
  ReprocessResponse,
  BackfillSummariesResponse,
  RequeueFailedTasksResponse,
  ResummarizeSessionResponse,
  SessionDetail,
  SessionMatch,
  SessionSummaryResponse,
  TaskQueueStatus,
  TimeGranularity,
  TimePeriod,
  TimeSeriesResponse,
  TokenSeriesResponse,
  TokenByModelSeriesResponse,
  UserModelPricing,
} from '../types/api';

const API_BASE = '/api';

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-csrf-protection': '1',
      ...options?.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return response.json();
}

function granularityToProto(granularity: TimeGranularity): string {
  switch (granularity) {
    case 'daily':
      return 'TIME_GRANULARITY_DAILY';
    case 'weekly':
      return 'TIME_GRANULARITY_WEEKLY';
    case 'monthly':
      return 'TIME_GRANULARITY_MONTHLY';
    default:
      return 'TIME_GRANULARITY_DAILY';
  }
}

function periodToProto(period: TimePeriod): string {
  switch (period) {
    case 'today':
      return 'TIME_PERIOD_TODAY';
    case '7days':
      return 'TIME_PERIOD_7_DAYS';
    case '30days':
      return 'TIME_PERIOD_30_DAYS';
    case 'all':
      return 'TIME_PERIOD_ALL';
    default:
      return 'TIME_PERIOD_7_DAYS';
  }
}

export interface CurrentUser {
  email: string;
  name: string;
  authenticated: boolean;
}

export const api = {
  async getCurrentUser(): Promise<CurrentUser> {
    return fetchApi<CurrentUser>('/auth/me');
  },

  async getDashboard(period: TimePeriod = '7days'): Promise<DashboardResponse> {
    const protoPeriod = periodToProto(period);
    return fetchApi<DashboardResponse>(`/insights/dashboard?period=${protoPeriod}`);
  },

  async getFilterOptions(): Promise<FilterOptionsResponse> {
    return fetchApi<FilterOptionsResponse>('/insights/filters');
  },

  async listSessions(params: ListSessionsParams = {}): Promise<ListSessionsResponse> {
    const searchParams = new URLSearchParams();
    if (params.hostname) searchParams.set('hostname', params.hostname);
    if (params.project) searchParams.set('project', params.project);
    if (params.period) searchParams.set('period', periodToProto(params.period));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    if (params.anomalyOnly) searchParams.set('anomaly_only', 'true');
    if (params.frictionOnly) searchParams.set('friction_only', 'true');
    if (params.source) searchParams.set('source', params.source);

    const query = searchParams.toString();
    return fetchApi<ListSessionsResponse>(`/insights/sessions${query ? `?${query}` : ''}`);
  },

  async getSession(sessionId: string): Promise<SessionDetail> {
    const res = await fetchApi<{ session: SessionDetail }>(
      `/insights/sessions/${encodeURIComponent(sessionId)}`
    );
    return res.session;
  },

  async resolveSessionId(prefix: string): Promise<SessionMatch[]> {
    const res = await fetchApi<{ matches: SessionMatch[] }>(
      `/insights/resolve/${encodeURIComponent(prefix)}`
    );
    return res.matches || [];
  },

  async getSessionSummary(sessionId: string): Promise<SessionSummaryResponse> {
    return fetchApi<SessionSummaryResponse>(
      `/insights/sessions/${encodeURIComponent(sessionId)}/summary`
    );
  },

  // Admin endpoints
  async getReprocessingStatus(): Promise<ReprocessingStatus> {
    return fetchApi<ReprocessingStatus>('/admin/reprocess/status');
  },

  async reprocessSessions(
    forceAll: boolean = false,
  ): Promise<ReprocessResponse> {
    return fetchApi<ReprocessResponse>('/admin/reprocess', {
      method: 'POST',
      body: JSON.stringify({ forceAll }),
    });
  },

  async backfillSummaries(limit: number): Promise<BackfillSummariesResponse> {
    return fetchApi<BackfillSummariesResponse>('/admin/backfill/summaries', {
      method: 'POST',
      body: JSON.stringify({ limit }),
    });
  },

  // Task Queue endpoints
  async getTaskQueueStatus(): Promise<TaskQueueStatus> {
    return fetchApi<TaskQueueStatus>('/admin/tasks/status');
  },

  async listTasks(params: ListTasksParams = {}): Promise<ListTasksResponse> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    if (params.taskType) searchParams.set('taskType', params.taskType);
    if (params.uploadId) searchParams.set('uploadId', params.uploadId);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return fetchApi<ListTasksResponse>(`/admin/tasks${query ? `?${query}` : ''}`);
  },

  async getTimeSeries(period: TimePeriod = '7days', granularity: TimeGranularity = 'daily', project?: string): Promise<TimeSeriesResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('period', periodToProto(period));
    searchParams.set('granularity', granularityToProto(granularity));
    if (project) searchParams.set('project', project);
    return fetchApi<TimeSeriesResponse>(`/insights/timeseries?${searchParams.toString()}`);
  },

  // getTokenSeries returns per-day token usage, correctly attributed to the
  // day tokens were consumed (via the session_token_usage_daily rollup)
  // rather than the day a session started. Always daily granularity.
  async getTokenSeries(period: TimePeriod = '7days', project?: string): Promise<TokenSeriesResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('period', periodToProto(period));
    if (project) searchParams.set('project', project);
    return fetchApi<TokenSeriesResponse>(`/insights/tokenseries?${searchParams.toString()}`);
  },

  // getTokenSeriesByModel returns per-day, per-model token totals (long
  // format: one row per bucket+model) from the session_model_tokens_daily
  // rollup. The caller pivots the rows into per-bucket stacked series for the
  // "Total Tokens by Model" card. Always daily granularity.
  async getTokenSeriesByModel(
    period: TimePeriod = '7days',
    project?: string
  ): Promise<TokenByModelSeriesResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('period', periodToProto(period));
    if (project) searchParams.set('project', project);
    return fetchApi<TokenByModelSeriesResponse>(
      `/insights/tokenseries/bymodel?${searchParams.toString()}`
    );
  },

  // getActivitySeries returns per-day tool-call and turn activity, correctly
  // attributed to the day the activity occurred (via the
  // session_activity_daily rollup) rather than the day a session started.
  // Always daily granularity.
  async getActivitySeries(period: TimePeriod = '7days', project?: string): Promise<ActivitySeriesResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('period', periodToProto(period));
    if (project) searchParams.set('project', project);
    return fetchApi<ActivitySeriesResponse>(`/insights/activityseries?${searchParams.toString()}`);
  },

  async resummarizeSession(sessionId: string): Promise<ResummarizeSessionResponse> {
    return fetchApi<ResummarizeSessionResponse>(
      `/admin/sessions/${encodeURIComponent(sessionId)}/resummarize`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  },

  // Daily agent-hours + parallelism for the activity strip / Usage view.
  async getAgentActivity(period: TimePeriod = '7days'): Promise<AgentActivity> {
    return fetchApi<AgentActivity>(`/insights/activity?period=${periodToProto(period)}`);
  },

  // The four Agent Keys for the Home panel.
  async getAgentKeys(period: TimePeriod = '7days'): Promise<AgentKeys> {
    return fetchApi<AgentKeys>(`/insights/keys?period=${periodToProto(period)}`);
  },

  // Sessions needing attention for the Home card.
  async getNeedsALook(period: TimePeriod = '7days'): Promise<GetNeedsALookResponse> {
    return fetchApi<GetNeedsALookResponse>(`/insights/needs-a-look?period=${periodToProto(period)}`);
  },

  // Turns per (project, day) for the Home heatmap.
  async getProjectHeatmap(period: TimePeriod = '7days'): Promise<GetProjectHeatmapResponse> {
    return fetchApi<GetProjectHeatmapResponse>(`/insights/project-heatmap?period=${periodToProto(period)}`);
  },

  // Per-project readiness cards for the Projects page.
  async listProjectReadiness(period: TimePeriod = '30days'): Promise<ListProjectReadinessResponse> {
    return fetchApi<ListProjectReadinessResponse>(`/insights/projects?period=${periodToProto(period)}`);
  },

  // The Usage-tab economics: value series, by model/project, waste ledger.
  async getUsageEconomics(period: TimePeriod = '7days'): Promise<UsageEconomics> {
    return fetchApi<UsageEconomics>(`/insights/usage?period=${periodToProto(period)}`);
  },

  // The consolidated Usage screen (per-day driving hours + agent lanes, spend by
  // model/project, the overhead ledger, heaviest sessions) for a range in one
  // payload. Leverage, deltas and per-hour rates are derived client-side.
  async getUsage(period: TimePeriod = '7days'): Promise<UsageResponse> {
    return fetchApi<UsageResponse>(`/insights/usage-summary?period=${periodToProto(period)}`);
  },

  // Usage economics summary for the Home headline (mode-aware rendering).
  async getSpendSummary(period: TimePeriod = '7days'): Promise<SpendSummary> {
    return fetchApi<SpendSummary>(`/insights/spend?period=${periodToProto(period)}`);
  },

  // Daemon sync status for the authenticated user — powers Home's
  // never-connected / stale-daemon states. totalFiles/lastSyncTimestamp are
  // omitted when zero, so callers must default the absent case.
  async getSyncStatus(): Promise<SyncStatus> {
    return fetchApi<SyncStatus>('/logsync/status');
  },

  // Lightweight daemon connectivity for Home — whether the user has ever synced
  // and when last. Avoids GetSyncStatus's DISTINCT-ON-over-uploads scan on the
  // landing page (#774). hasAnyUpload/lastSyncTimestamp are omitted when
  // false/zero, so callers must default the absent case.
  async getConnectionStatus(): Promise<ConnectionStatus> {
    return fetchApi<ConnectionStatus>('/logsync/connection');
  },

  async getUserSettings(): Promise<UserSettings> {
    const res = await fetchApi<{ settings: UserSettings }>('/admin/settings');
    return res.settings ?? {};
  },

  async updateUserSettings(settings: UserSettings): Promise<void> {
    await fetchApi('/admin/settings', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    });
  },

  // Per-user model-price overrides (#829). grpc-gateway omits zero-valued
  // doubles, so normalize absent rates to 0 here — the editor always sees a
  // complete row (a $0 free-provider plan keeps its prefix with all-zero rates).
  async listUserModelPricing(): Promise<UserModelPricing[]> {
    const res = await fetchApi<{
      rates?: Array<Partial<UserModelPricing> & { modelPrefix: string }>;
    }>('/admin/settings/model-pricing');
    return (res.rates ?? []).map((r) => ({
      modelPrefix: r.modelPrefix,
      inputPerMtok: r.inputPerMtok ?? 0,
      outputPerMtok: r.outputPerMtok ?? 0,
      cacheWritePerMtok: r.cacheWritePerMtok ?? 0,
      cacheReadPerMtok: r.cacheReadPerMtok ?? 0,
      cacheWrite5mPerMtok: r.cacheWrite5mPerMtok ?? 0,
    }));
  },

  async upsertUserModelPricing(rate: UserModelPricing): Promise<void> {
    await fetchApi('/admin/settings/model-pricing', {
      method: 'POST',
      body: JSON.stringify({ rate }),
    });
  },

  // modelPrefix is a body field (never a path param — it may contain slashes).
  async deleteUserModelPricing(modelPrefix: string): Promise<void> {
    await fetchApi('/admin/settings/model-pricing/delete', {
      method: 'POST',
      body: JSON.stringify({ modelPrefix }),
    });
  },

  async listSemanticPatterns(view?: 'resolved'): Promise<ListSemanticPatternsResponse> {
    return fetchApi<ListSemanticPatternsResponse>(view ? `/insights/patterns?view=${view}` : '/insights/patterns');
  },

  // Record accept/reject feedback on an insight. 'dismiss' hides it, 'accept'
  // pins it, 'clear' resets.
  async setPatternFeedback(id: string, feedback: 'dismiss' | 'accept' | 'clear'): Promise<void> {
    await fetchApi(`/insights/patterns/${encodeURIComponent(id)}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
  },

  // The user's recurring-constraint candidates — the "direction" coaching axis.
  async listConstraintCandidates(): Promise<ListConstraintCandidatesResponse> {
    return fetchApi<ListConstraintCandidatesResponse>('/insights/constraint-candidates');
  },

  // Persistently hide a constraint candidate by its durable cluster_key.
  // cluster_key is a body field (never a path param — it may contain slashes).
  async dismissConstraintCandidate(clusterKey: string): Promise<void> {
    await fetchApi('/insights/constraint-candidates/dismiss', {
      method: 'POST',
      body: JSON.stringify({ clusterKey }),
    });
  },

  async generateInsightsReport(period: TimePeriod, kind?: 'weekly' | 'monthly'): Promise<GenerateInsightsReportResponse> {
    return fetchApi<GenerateInsightsReportResponse>('/insights/reports/generate', {
      method: 'POST',
      body: JSON.stringify({ period: periodToProto(period), kind: kind ?? 'weekly' }),
    });
  },

  async getInsightsReport(reportId: string): Promise<InsightsReport> {
    const res = await fetchApi<{ report: InsightsReport }>(
      `/insights/reports/${encodeURIComponent(reportId)}`
    );
    return res.report;
  },

  async listInsightsReports(params: ListInsightsReportsParams = {}): Promise<ListInsightsReportsResponse> {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    if (params.kind) searchParams.set('kind', params.kind);
    const query = searchParams.toString();
    return fetchApi<ListInsightsReportsResponse>(`/insights/reports${query ? `?${query}` : ''}`);
  },

  async requeueFailedTasks(
    params: { taskTypes?: string[]; errorPattern?: string } = {}
  ): Promise<RequeueFailedTasksResponse> {
    return fetchApi<RequeueFailedTasksResponse>('/admin/tasks/requeue_failed', {
      method: 'POST',
      body: JSON.stringify({
        taskTypes: params.taskTypes ?? [],
        errorPattern: params.errorPattern ?? '',
      }),
    });
  },
};

export { ApiError };
